import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSafe } from '../context/SafeContext';
import { useTheme, type ThemeChoice } from '../theme/useTheme';
import { useEntitlement } from '../hooks/useEntitlement';
import { HbsButton } from '../components/hbs/Button';
import { ChangePasswordModal } from '../components/ChangePasswordModal';
import { BiometricUnlockSection } from '../components/BiometricUnlockSection';
import { EmailNotVerifiedModal } from '../components/EmailNotVerifiedModal';
import { DeleteAccountSection } from '../components/DeleteAccountSection';
import { createSubscribeHandoffUrl } from '../api/subscribeHandoff';

const THEME_OPTIONS: { value: ThemeChoice; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

export default function SettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const { loginEmail, logout, emailVerified } = useSafe();
  const { theme, resolved, setTheme } = useTheme();
  const { state, trialDaysLeft } = useEntitlement();
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [emailNotVerifiedOpen, setEmailNotVerifiedOpen] = useState(false);
  const [handoffLoading, setHandoffLoading] = useState(false);
  const [handoffError, setHandoffError] = useState<string | null>(null);

  async function handleSignOut(): Promise<void> {
    await logout();
    navigate('/auth', { replace: true });
  }

  // Same teardown path as Sign out, used after a successful account deletion.
  // The server has already destroyed the account by the time runDelete-
  // AccountHandshake() returns 'success', so further authenticated requests
  // with the now-orphaned JWT would 401 — we must clear in-memory state and
  // navigate before any other code observes the page.
  async function handleAccountDeleted(): Promise<void> {
    await logout();
    navigate('/auth', { replace: true });
  }

  async function handleManageSubscription(): Promise<void> {
    setHandoffError(null);
    setHandoffLoading(true);
    try {
      const url = await createSubscribeHandoffUrl();
      window.location.href = url;
    } catch (e) {
      setHandoffError(`Could not open subscription manager: ${(e as Error).message}`);
    } finally {
      setHandoffLoading(false);
    }
  }

  return (
    <div className="p-9 max-w-2xl mx-auto flex flex-col gap-8">
      <h1 className="font-display font-semibold text-[32px] tracking-hbs-tight text-hbs-text">
        Settings
      </h1>

      {/* Account */}
      <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6">
        <h2 className="font-display text-[18px] text-hbs-text mb-4">Account</h2>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1">
              Email
            </p>
            <p className="text-[15px] text-hbs-text truncate">
              {loginEmail ?? '—'}
            </p>
            {emailVerified === false && (
              <div className="mt-2 flex items-center gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                <span aria-hidden="true">⚠</span>
                <span>Email not verified.</span>
                <button
                  type="button"
                  id="resend-verification-email-button"
              onClick={() => setEmailNotVerifiedOpen(true)}
                  className="text-amber-900 underline hover:text-amber-700"
                >
                  Resend verification email
                </button>
              </div>
            )}
          </div>
          <HbsButton variant="subtle" id="sign-out-button" onClick={handleSignOut}>
            Sign out
          </HbsButton>
        </div>
        <div className="border-t border-hbs-border mt-5 pt-5 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1">
              Safe password
            </p>
            <p className="text-[13px] text-hbs-text-mute">
              The password you use to unlock your safe.
            </p>
          </div>
          <HbsButton variant="subtle" id="change-safe-password-button" onClick={() => setPwModalOpen(true)}>
            Change safe password
          </HbsButton>
        </div>
      </section>

      {/* Biometric unlock (renders nothing on unsupported platforms) */}
      <BiometricUnlockSection />

      {/* Appearance */}
      <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6">
        <h2 className="font-display text-[18px] text-hbs-text mb-4">Appearance</h2>
        <p className="text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-2">
          Theme
        </p>
        <div className="flex gap-2">
          {THEME_OPTIONS.map((opt) => {
            const active = theme === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                id={`theme-option-${opt.value}`}
                onClick={() => setTheme(opt.value)}
                className={`flex-1 px-3 py-2 rounded-hbs-sm text-[13px] font-semibold transition-colors ${
                  active
                    ? 'bg-hbs-primary text-hbs-on-primary'
                    : 'bg-hbs-surface-alt text-hbs-text hover:bg-hbs-surface'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <p className="text-[12px] text-hbs-text-mute mt-2">
          Currently using <span className="font-semibold text-hbs-text">{resolved}</span> theme.
        </p>
      </section>

      {/* Subscription */}
      <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6">
        <h2 className="font-display text-[18px] text-hbs-text mb-4">Subscription</h2>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1">
              Billing &amp; plan
            </p>
            <p className="text-[13px] text-hbs-text-mute">
              View invoices, change your plan, or cancel.
            </p>
            {handoffError && (
              <p className="text-[12px] text-red-500 mt-1">{handoffError}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <HbsButton
              variant="subtle"
              id="manage-subscription-button"
              onClick={() => { void handleManageSubscription(); }}
              disabled={handoffLoading}
            >
              {handoffLoading ? 'Opening…' : 'Manage subscription'}
            </HbsButton>
            {state === 'trial' && trialDaysLeft !== null && (
              <p className="text-xs text-ink-muted">
                Trial — {trialDaysLeft} {trialDaysLeft === 1 ? 'day' : 'days'} left
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Danger zone — keep last. */}
      <DeleteAccountSection onAccountDeleted={handleAccountDeleted} />

      <p className="text-[11px] text-hbs-text-mute text-center mt-8" aria-label="Build identifier">
        v{__APP_VERSION__} ({__GIT_SHA__})
      </p>

      <ChangePasswordModal open={pwModalOpen} onClose={() => setPwModalOpen(false)} />
      {emailNotVerifiedOpen && loginEmail && (
        <EmailNotVerifiedModal
          email={loginEmail}
          onClose={() => setEmailNotVerifiedOpen(false)}
        />
      )}
    </div>
  );
}
